<template>
  <a-modal
    :open="visible"
    :confirm-loading="loading"
    :footer="null"
    :width="600"
    @cancel="handleCancel"
  >
    <template #title>
      <div class="modal-title">
        {{ mode === 'create' ? '쿠폰 공급사 등록' : '쿠폰 공급사 수정' }}
      </div>
    </template>

    <div class="modal-content">
      <a-form
        ref="formRef"
        :model="formData"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <!-- Provider Selection -->
        <a-form-item label="공급사" required>
          <a-select
            v-model:value="formData.provider"
            placeholder="공급사 선택"
            :disabled="mode === 'edit'"
            @change="handleProviderChange"
          >
            <a-select-option value="giftiel">기프티엘 (Giftiel)</a-select-option>
            <a-select-option value="sendb">센드비 (Sendb)</a-select-option>
          </a-select>
        </a-form-item>

        <!-- Display Name -->
        <a-form-item label="표시명" required>
          <a-input
            v-model:value="formData.displayName"
            placeholder="화면에 표시될 이름 (예: 써마지 - 기프티엘)"
          />
        </a-form-item>

        <!-- Contractor Info -->
        <a-form-item label="계약사 코드" required>
          <a-input
            v-model:value="formData.contractorCode"
            placeholder="계약사 고유 코드"
          />
        </a-form-item>

        <a-form-item label="계약사명" required>
          <a-input
            v-model:value="formData.contractorName"
            placeholder="계약사 이름 (예: 써마지, 서티라이프)"
          />
        </a-form-item>

        <!-- Credentials Section - Giftiel -->
        <template v-if="formData.provider === 'giftiel'">
          <a-divider>기프티엘 인증정보</a-divider>
          <a-form-item label="CI Code" required>
            <a-input
              v-model:value="formData.credentials.ciCode"
              placeholder="기프티엘 CI 코드"
            />
          </a-form-item>
          <a-form-item label="CI Password" required>
            <a-input-password
              v-model:value="formData.credentials.ciPwd"
              placeholder="기프티엘 CI 비밀번호"
            />
          </a-form-item>
        </template>

        <!-- Credentials Section - Sendb -->
        <template v-if="formData.provider === 'sendb'">
          <a-divider>센드비 인증정보</a-divider>
          <a-form-item label="API Key" required>
            <a-input
              v-model:value="formData.credentials.apiKey"
              placeholder="센드비 API 키 (암호화 키 겸용)"
            />
          </a-form-item>
          <a-form-item label="IV" required>
            <a-input
              v-model:value="formData.credentials.iv"
              placeholder="센드비 암호화 IV"
            />
          </a-form-item>
        </template>

        <!-- Additional Settings -->
        <a-divider>추가 설정</a-divider>

        <a-form-item label="정렬순서">
          <a-input-number
            v-model:value="formData.sortOrder"
            :min="0"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item label="설명">
          <a-textarea
            v-model:value="formData.description"
            placeholder="메모 또는 설명"
            :rows="3"
          />
        </a-form-item>

        <a-form-item label="활성 상태">
          <a-switch
            v-model:checked="formData.isActive"
            checked-children="활성"
            un-checked-children="비활성"
          />
        </a-form-item>
      </a-form>

      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
        <a-button type="primary" :loading="loading" @click="handleSubmit" class="yellow-button">
          {{ mode === 'create' ? '등록' : '수정' }}
        </a-button>
      </div>
    </div>
  </a-modal>
</template>

<script>
import { message } from 'ant-design-vue';
import couponProviderApis from './api';

export default {
  name: 'CouponProviderModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create'
    },
    contract: {
      type: Object,
      default: null
    }
  },
  emits: ['update:visible', 'success'],
  data() {
    return {
      api: null,
      loading: false,
      formData: this.getInitialFormData()
    };
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        if (this.mode === 'edit' && this.contract) {
          this.formData = {
            provider: this.contract.provider,
            providerName: this.contract.providerName || '',
            displayName: this.contract.displayName,
            contractorCode: this.contract.contractorCode,
            contractorName: this.contract.contractorName,
            credentials: { ...this.contract.credentials },
            sortOrder: this.contract.sortOrder || 0,
            description: this.contract.description || '',
            isActive: this.contract.isActive !== false
          };
        } else {
          this.formData = this.getInitialFormData();
        }
      }
    }
  },
  created() {
    this.api = couponProviderApis(this.$apiInstance, this.$store);
  },
  methods: {
    getInitialFormData() {
      return {
        provider: undefined,
        providerName: '',
        displayName: '',
        contractorCode: '',
        contractorName: '',
        credentials: {},
        sortOrder: 0,
        description: '',
        isActive: true
      };
    },

    handleProviderChange(provider) {
      if (provider === 'giftiel') {
        this.formData.credentials = { ciCode: '', ciPwd: '' };
        this.formData.providerName = '기프티엘';
      } else if (provider === 'sendb') {
        this.formData.credentials = { apiKey: '', iv: '' };
        this.formData.providerName = '센드비';
      } else {
        this.formData.credentials = {};
        this.formData.providerName = '';
      }
    },

    validateForm() {
      if (!this.formData.provider) {
        message.warning('공급사를 선택해주세요.');
        return false;
      }
      if (!this.formData.displayName) {
        message.warning('표시명을 입력해주세요.');
        return false;
      }
      if (!this.formData.contractorCode) {
        message.warning('계약사 코드를 입력해주세요.');
        return false;
      }
      if (!this.formData.contractorName) {
        message.warning('계약사명을 입력해주세요.');
        return false;
      }

      if (this.formData.provider === 'giftiel') {
        if (!this.formData.credentials.ciCode) {
          message.warning('CI Code를 입력해주세요.');
          return false;
        }
        if (!this.formData.credentials.ciPwd) {
          message.warning('CI Password를 입력해주세요.');
          return false;
        }
      } else if (this.formData.provider === 'sendb') {
        if (!this.formData.credentials.apiKey) {
          message.warning('API Key를 입력해주세요.');
          return false;
        }
        if (!this.formData.credentials.iv) {
          message.warning('IV를 입력해주세요.');
          return false;
        }
      }

      return true;
    },

    handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.loading = true;
      const data = { ...this.formData };

      // CreateDTO에서는 isActive를 지원하지 않음
      if (this.mode === 'create') {
        delete data.isActive;
        this.api.createContract(
          data,
          () => {
            message.success('등록되었습니다.');
            this.loading = false;
            this.$emit('success');
          },
          (error) => {
            message.error('등록 실패: ' + (error.response?.data?.message || error.message));
            this.loading = false;
          }
        );
      } else {
        this.api.updateContract(
          this.contract._id,
          data,
          () => {
            message.success('수정되었습니다.');
            this.loading = false;
            this.$emit('success');
          },
          (error) => {
            message.error('수정 실패: ' + (error.response?.data?.message || error.message));
            this.loading = false;
          }
        );
      }
    },

    handleCancel() {
      this.$emit('update:visible', false);
    }
  }
};
</script>

<style scoped>
.modal-title {
  font-size: 16px;
  font-weight: 600;
}

.modal-content {
  padding: 16px 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.yellow-button {
  background-color: #faad14 !important;
  border-color: #faad14 !important;
}

.yellow-button:hover {
  background-color: #ffc53d !important;
  border-color: #ffc53d !important;
}
</style>
